/** ECRANUL «PROGRAM»: durata și efectivele proiectului, din lista de cantități.
 *
 *  Patru vederi: GRAFICUL (șantierul și fiecare corp cu nivelurile lui pe axa
 *  timpului — liniile de echilibru se citesc ca diagonale; o bară apăsată își
 *  arată etapa, un corp apăsat își arată macaralele și așteptarea), RESURSELE (histograma pe săptămâni, muncitorii pe categorie cu
 *  efectivul recomandat și cel pus de om, macaralele pe corp), PÂRGHIILE
 *  (restricțiile fizice ale proiectului, cu care e atinsă; pârghiile care
 *  scurtează programul, aplicate, disponibile sau refuzate cu motivul, fiecare
 *  cu costul și riscul ei; costurile a ce e activ), ȚINTA (optimizatorul:
 *  «cu atâtea luni mai repede, cât mai ieftin» sau «cel mai bun ROI anual» →
 *  pârghiile găsite, traiectoria, refuzurile cu motiv, scenariul din plan) și
 *  REGULILE (etapele cu lag-urile lor, ritmul structurii, calendarul).
 *
 *  Regulile sunt ale PROIECTULUI (`cost.program`, cu `rev`); efectivele puse de
 *  om pe ecranul Resurse și pârghiile alese pe ecranul Pârghii fac un SCENARIU
 *  NOU cu `program.muncitori` / `program.parghii`, ca orice «și dacă» —
 *  scenariul părăsit rămâne. Calculul e tot al serviciului
 *  (`control-plane/api/program.py`): aici doar se citește și se desenează. */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Termeni, { Lamurire } from "./Termeni";
import SeIncarca from "../../components/SeIncarca";
import { data, numar, useT } from "../../i18n";
import type { Cheie } from "../../i18n";
import { ApiError } from "../../api";
import { nrSuprascrieri } from "./boq";
import type { Scenarii } from "./scenarii";
import {
  areProgram, axaTimpului, bareHistograma, culoareEtapei, cuMuncitorii, cuParghia, getProgram, getSetariProgram, grupuriGantt,
  nivelInCorp, numeleNivelului, optimizeazaProgram, putSetariProgram, stareaMeseriei, subSegmente, TONUL_FAZEI,
  type EtapaCatalog, type EtapaProgram, type FazaPachet, type GrupGantt, type ImpliciteProgram, type ParghieProgram, type Program,
  type RezultatOptimizare, type SetariProgram, type SetariProiect, type PerioadaMacarale,
  type RegulaProgram,
} from "./program";
import "./program.css";

type Vedere = "grafic" | "resurse" | "parghii" | "tinta" | "reguli";
const LUNI: Cheie[] = ["prog.luna.0", "prog.luna.1", "prog.luna.2", "prog.luna.3", "prog.luna.4", "prog.luna.5",
                       "prog.luna.6", "prog.luna.7", "prog.luna.8", "prog.luna.9", "prog.luna.10", "prog.luna.11"];
const ZILE: Cheie[] = ["prog.zi.0", "prog.zi.1", "prog.zi.2", "prog.zi.3", "prog.zi.4", "prog.zi.5", "prog.zi.6"];
const ORDINEA_ETAPELOR = ["E00", "E10", "E20", "E25", "E30", "E40", "E45", "E50", "E60", "E65", "E70", "E80", "E85",
                          "E90", "E95", "E97", "E99", "E100"];
const nr = (v: number | null | undefined, zec = 0) => numar(v, { maximumFractionDigits: zec });

/** Scenariul pe care se cere programul: numai unul care schimbă ceva — în
 *  rețete și prețuri (lista lui) sau în program; altfel baza. */
export function scenariulProgramului(sc: Scenarii): string | null {
  return areProgram(sc.curent.program) || nrSuprascrieri(sc.curent.catalog) > 0 ? sc.curent.id : null;
}

export default function PanouProgram({ projectId, solutionId, sc, potScrie }: {
  projectId: string;
  /** soluția cu lista de cantități (cea a listei) */
  solutionId: string | null;
  sc: Scenarii;
  potScrie: boolean;
}) {
  const t = useT();
  const [vedere, setVedere] = useState<Vedere>("grafic");
  const [program, setProgram] = useState<Program | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [seIncarca, setSeIncarca] = useState(false);
  const [setari, setSetari] = useState<SetariProiect | null>(null);
  const scenariu = scenariulProgramului(sc);
  const amprentaSc = JSON.stringify([sc.curent.program ?? null, sc.curent.catalog ?? null]);

  const incarca = useCallback(async (refa = false) => {
    if (!solutionId) { setProgram(null); return; }
    setSeIncarca(true);
    setEroare(null);
    try {
      setProgram(await getProgram(projectId, solutionId, { scenariu, refa }));
    } catch (e) {
      setProgram(null);
      setEroare(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSeIncarca(false);
    }
  // amprentaSc: scenariul curent și-a schimbat programul sau catalogul
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, solutionId, scenariu, amprentaSc]);
  useEffect(() => { void incarca(); }, [incarca]);

  const incarcaSetari = useCallback(async () => {
    try { setSetari(await getSetariProgram(projectId)); } catch { setSetari(null); }
  }, [projectId]);
  useEffect(() => { void incarcaSetari(); }, [incarcaSetari]);

  if (!solutionId) return <div className="pg"><span className="bc-um">{t("prog.fara_solutie")}</span></div>;

  return (
    <div className="pg">
      <div className="pg-bara">
        <div className="tabs" role="tablist">
          {(["grafic", "resurse", "parghii", "tinta", "reguli"] as Vedere[]).map((v) => (
            <button key={v} type="button" role="tab" aria-selected={vedere === v} className={vedere === v ? "on" : ""}
                    onClick={() => setVedere(v)}>{t(`prog.sub.${v}`)}</button>
          ))}
        </div>
        <span className="bc-um">
          {scenariu ? t("prog.scenariul", { n: sc.curent.nume }) : t("prog.pe_baza")}
        </span>
        <span style={{ flex: "1 1 auto" }} />
        {program && <button type="button" className="btn btng" onClick={() => void incarca(true)}>{t("prog.refa")}</button>}
      </div>
      {eroare && <span className="ip-err">{t("prog.eroare", { m: eroare })}</span>}
      {seIncarca && !program && <SeIncarca randuri={8} />}
      {program && <Kpi p={program} setari={setari} />}
      {program && vedere === "grafic" && <Grafic p={program} />}
      {program && vedere === "resurse" && (
        <Resurse p={program} sc={sc} potScrie={potScrie} />
      )}
      {program && vedere === "parghii" && (
        <Parghii p={program} sc={sc} potScrie={potScrie} />
      )}
      {program && vedere === "tinta" && (
        <Tinta p={program} projectId={projectId} solutionId={solutionId} scenariu={scenariu} sc={sc} potScrie={potScrie} />
      )}
      {vedere === "reguli" && (
        <Reguli projectId={projectId} setari={setari} potScrie={potScrie} reguli={program?.reguli ?? setari?.implicite.reguli ?? null}
                dupaSalvare={() => { void incarcaSetari(); void incarca(); }} />
      )}
    </div>
  );
}

// --- antetul ----------------------------------------------------------------

function Kpi({ p, setari }: { p: Program; setari: SetariProiect | null }) {
  const t = useT();
  const startPus = !!setari?.data.start || !!p.setari.start && p.setari.start !== setari?.implicite.setari.start;
  return (
    <div className="pg-kpi">
      <div><div className="l">{t("prog.kpi.inceput")}</div><div className="v mono">{data(p.start)}</div>
        <div className="s">{startPus ? t("prog.kpi.start_pus") : t("prog.kpi.start_implicit")}</div></div>
      <div><div className="l">{t("prog.kpi.sfarsit")}</div><div className="v mono">{data(p.sfarsit)}</div>
        <div className="s">{p.etape[p.etape.length - 1]?.nume ?? ""}</div></div>
      <div><div className="l">{t("prog.kpi.durata")}</div><div className="v">{t("prog.kpi.luni", { n: nr(p.luni, 1) })}</div>
        <div className="s">{t("prog.kpi.zile_lucratoare", { n: nr(p.zile_lucratoare) })}</div></div>
      <div><div className="l">{t("prog.kpi.muncitori")}</div><div className="v">{t("prog.kpi.la_varf", { n: nr(p.muncitori_varf) })}</div>
        <div className="s">{t("prog.kpi.in_medie", { n: nr(p.muncitori_medie), m: nr(Object.keys(p.meserii).length) })}</div></div>
      <div><div className="l">{t("prog.kpi.macarale")}</div><div className="v"><Termeni text={t("prog.kpi.luni_macara", { n: nr(p.macarale.luni_macara, 1) })} /></div>
        <div className="s"><Termeni text={t("prog.kpi.fronturi", { n: nr(p.macarale.fronturi_max), p: nr(p.macarale.plafon) })} /></div></div>
      {p.camioane && p.camioane.zile_camion > 0 && (
        <div><div className="l">{t("prog.kpi.camioane")}</div>
          <div className="v">{t("prog.kpi.zile_camion", { n: nr(p.camioane.zile_camion) })}</div>
          <div className="s">{p.camioane.zona
            ? t("prog.kpi.camioane_varf", { n: nr(p.camioane.varf_pe_zi ?? 0, 1), z: p.camioane.zona })
            : t("prog.kpi.camioane_varf_fara", { n: nr(p.camioane.varf_pe_zi ?? 0, 1) })}</div></div>
      )}
      {(p.cost_suplimentar_eur ?? 0) > 0 && (
        <div><div className="l">{t("prog.kpi.cost_parghii")}</div><div className="v mono">{nr(p.cost_suplimentar_eur)} €</div>
          <div className="s">{t("prog.kpi.cost_parghii_sub", { n: nr((p.costuri_suplimentare ?? []).length) })}</div></div>
      )}
      {(p.restrictii_atinse ?? []).length > 0 && (
        <div><div className="l">{t("prog.kpi.restrictii")}</div>
          <div className="v">{t("prog.kpi.restrictii_atinse", { n: nr((p.restrictii_atinse ?? []).length) })}</div>
          <div className="s">{(p.restrictii ?? []).filter((r) => r.atinsa).map((r) => r.nume).join(" · ")}</div></div>
      )}
    </div>
  );
}

// --- restricțiile și pârghiile -------------------------------------------------

/** Restricțiile fizice ale proiectului și pârghiile care scurtează programul:
 *  fiecare pârghie cu ce câștigă, ce costă, ce riscă și, pe programul ăsta, dacă
 *  e aplicată, disponibilă sau refuzată cu motivul; o pârghie aleasă face un
 *  scenariu nou cu `program.parghii`. */
function Parghii({ p, sc, potScrie }: { p: Program; sc: Scenarii; potScrie: boolean }) {
  const t = useT();
  const restrictii = p.restrictii ?? [];
  const parghii = p.parghii ?? [];
  const costuri = p.costuri_suplimentare ?? [];
  const aplica = (x: ParghieProgram) => {
    const program = cuParghia(sc.curent.program, x);
    void sc.creeaza({}, { nume: t("prog.scenariu_parghie", { n: x.nume }), autor: "om",
                          nota: t("prog.scenariu_parghie_nota", { n: x.nume }), program }).catch(() => {});
  };
  if (!restrictii.length && !parghii.length) {
    return <div className="pg-bara"><span className="bc-um">{t("prog.parghii.fara")}</span></div>;
  }
  return (
    <>
      <div className="pg-doua">
        <section className="ip-card" aria-labelledby="pg-res-t">
          <div className="ip-card-h"><h2 id="pg-res-t">{t("prog.restrictii.titlu")}</h2>
            <Lamurire text={t("prog.restrictii.sub")} /></div>
          <ul className="pg-reguli">
            {restrictii.map((r) => (
              <li key={r.id} className={r.activa ? "" : "inactiva"}>
                <div>
                  <b>{r.nume}</b><Lamurire text={r.explicatie} />
                  {!r.activa ? <span className="ip-pill ip-pill-mut">{t("prog.restrictii.fara_date")}</span>
                    : r.atinsa ? <span className="ip-pill ip-pill-amb">{t("prog.restrictii.atinsa")}</span>
                    : <span className="ip-pill ip-pill-ok">{t("prog.restrictii.ok")}</span>}
                  {r.activa && r.valoare != null && r.limita != null && (
                    <span className="mono bc-um"> {t("prog.restrictii.valoare", { v: nr(r.valoare, 1), l: nr(r.limita, 1), um: r.um })}</span>
                  )}
                  {r.unde.length > 0 && <span className="bc-um"> · {r.unde.join(", ")}</span>}
                </div>
                {r.atinsa_text && <div className="pg-de-ce"><Termeni text={r.atinsa_text} /></div>}
              </li>
            ))}
          </ul>
        </section>
        <section className="ip-card" aria-labelledby="pg-cst-t">
          <div className="ip-card-h"><h2 id="pg-cst-t">{t("prog.costuri.titlu")}</h2>
            <Lamurire text={t("prog.costuri.sub")} /></div>
          {costuri.length === 0 ? <span className="bc-um">{t("prog.costuri.niciunul")}</span> : (
            <table className="sc-tab">
              <tbody>
                {costuri.map((c) => (
                  <tr key={c.id}><td><Termeni text={c.explicatie} /></td><td className="bc-n mono">{nr(c.eur)} €</td></tr>
                ))}
                <tr><td><b>{t("prog.costuri.total")}</b></td><td className="bc-n mono"><b>{nr(p.cost_suplimentar_eur ?? 0)} €</b></td></tr>
              </tbody>
            </table>
          )}
          {p.cost_macarale && (
            <div className="bc-um pg-de-ce">
              <Termeni text={t("prog.costuri.macarale", { eur: nr(p.cost_macarale.eur), l: nr(p.cost_macarale.luni_macara, 1),
                                                           b: nr(p.cost_macarale.buc), pl: nr(p.cost_macarale.pret_luna), pm: nr(p.cost_macarale.pret_mobilizare) })} />
            </div>
          )}
        </section>
      </div>
      <section className="ip-card" aria-labelledby="pg-par-t">
        <div className="ip-card-h"><h2 id="pg-par-t">{t("prog.parghii.titlu")}</h2>
          <Lamurire text={`${t("prog.parghii.intro")} ${t("prog.parghii.sub")}`} /></div>
        <ul className="pg-reguli pg-parghii">
          {parghii.map((x) => {
            const stare = x.aplicata ? "aplicata" : x.disponibila ? "disponibila" : "refuzata";
            return (
              <li key={x.id} className={stare === "refuzata" ? "inactiva" : ""}>
                <div>
                  <b>{x.nume}</b>
                  <Lamurire text={<><Termeni text={x.explicatie} /> <b>{t("prog.parghii.risc")}</b> <Termeni text={x.risc} /></>} />
                  <span className={`ip-pill ${stare === "aplicata" ? "ip-pill-acc" : stare === "disponibila" ? "ip-pill-ok" : "ip-pill-mut"}`}>
                    {t(`prog.parghii.${stare}`)}{x.aplicata && x.valoare_aplicata != null && x.valoare ? ` · ${nr(x.valoare_aplicata, 1)}` : ""}</span>
                  {x.limita_neverificata && <span className="ip-pill ip-pill-amb">{t("prog.parghii.neverificata")}</span>}
                  {potScrie && x.disponibila && (
                    <button type="button" className="btn btng" style={{ marginLeft: 8 }} disabled={sc.seSalveaza}
                            onClick={() => aplica(x)}>{t("prog.parghii.aplica")}</button>
                  )}
                </div>
                {x.motiv_refuz && <div className="pg-de-ce"><Termeni text={t("prog.parghii.motiv", { m: x.motiv_refuz })} /></div>}
                {x.cost && <div className="bc-um pg-de-ce"><b>{t("prog.parghii.cost")}</b> <Termeni text={x.cost} /></div>}
              </li>
            );
          })}
        </ul>
      </section>
    </>
  );
}

// --- graficul -----------------------------------------------------------------

/** «Tot proiectul» încape în lățimea ecranului; «Lună» și «Săptămână» țin o
 *  diviziune la cel puțin atâția pixeli — cât să-i încapă eticheta —, iar
 *  graficul mai lat decât ecranul se derulează pe orizontală, cu coloana
 *  nivelurilor și capul de tabel pe loc. */
type Zoom = "tot" | "luna" | "sapt";
const PX_LUNA = 64;
const PX_SAPTAMANA = 40;
/** coloana corpurilor și nivelurilor; ține în sincron cu `--pg-et` din program.css */
const LATIME_ETICHETE = 152;
/** sub atâția pixeli o bară nu-și mai scrie numele (rămâne în titlu și în detaliu) */
const PX_TEXT_PE_BARA = 64;

type Ales = { fel: "etapa"; e: EtapaProgram } | { fel: "grup"; cod: string } | null;

function Grafic({ p }: { p: Program }) {
  const t = useT();
  const [zoom, setZoom] = useState<Zoom>("tot");
  const [doarRitm, setDoarRitm] = useState(false);
  const [deschise, setDeschise] = useState<Set<string>>(
    () => new Set(["S", p.subsoluri?.[0]?.cod ?? "", p.corpuri[0]?.cod ?? ""]));
  const [ales, setAles] = useState<Ales>(null);
  const cutie = useRef<HTMLDivElement>(null);
  const [disponibil, setDisponibil] = useState(0);
  useLayoutEffect(() => {
    const el = cutie.current;
    if (!el) return;
    const masoara = () => setDisponibil(Math.max(el.clientWidth - LATIME_ETICHETE, 0));
    masoara();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(masoara);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const axa = useMemo(() => axaTimpului(p), [p]);
  const grupuri = useMemo(() => grupuriGantt(p, { santier: t("prog.santier"), nivel: numeleNivelului,
                                                  subsol: (n) => t("prog.subsol.regim", { n }) }), [p, t]);
  const latime = Math.max(disponibil, zoom === "luna" ? axa.luni.length * PX_LUNA
    : zoom === "sapt" ? axa.saptamani.length * PX_SAPTAMANA : 0);
  // la schimbarea zoomului, etapa aleasă rămâne în vedere (la o treime din
  // lățime); fără ea, aceeași dată rămâne la marginea din stânga
  const latimeVeche = useRef(latime);
  const etapaAleasa = ales?.fel === "etapa" ? ales.e : null;
  useLayoutEffect(() => {
    const el = cutie.current;
    if (el && latimeVeche.current > 0 && latimeVeche.current !== latime) {
      el.scrollLeft = etapaAleasa
        ? (etapaAleasa.zi_start / Math.max(p.zile_lucratoare, 1)) * latime - disponibil / 3
        : (el.scrollLeft / latimeVeche.current) * latime;
    }
    latimeVeche.current = latime;
  }, [latime]); // eslint-disable-line react-hooks/exhaustive-deps
  const px = (procent: number) => (procent / 100) * latime;
  const comuta = (cod: string) => {
    const n = new Set(deschise);
    if (n.has(cod)) n.delete(cod); else n.add(cod);
    setDeschise(n);
  };
  const toateDeschise = grupuri.every((g) => deschise.has(g.cod));
  const luniScurte = LUNI.map((k) => t(k));
  // capul de tabel: sus anii (în săptămâni, lunile), jos lunile (în săptămâni,
  // ziua cu care începe fiecare); o etichetă care nu încape se scurtează sau tace
  const sus = zoom === "sapt"
    ? axa.luni.map((d) => ({ d, text: px(d.latime) >= 64 ? `${luniScurte[d.data.getMonth()]} ${d.data.getFullYear()}`
      : px(d.latime) >= 28 ? luniScurte[d.data.getMonth()] : "" }))
    : axa.ani.map((d) => ({ d, text: px(d.latime) >= 36 ? String(d.data.getFullYear())
      : px(d.latime) >= 20 ? `'${String(d.data.getFullYear()).slice(2)}` : "" }));
  const jos = zoom === "sapt"
    ? axa.saptamani.map((d) => ({ d, text: px(d.latime) >= 18 ? String(d.data.getDate()) : "" }))
    : axa.luni.map((d) => {
      const nume = luniScurte[d.data.getMonth()];
      return { d, text: px(d.latime) >= 30 ? nume : px(d.latime) >= 12 ? nume.slice(0, 1).toUpperCase() : "" };
    });
  const liniiSlabe = zoom === "sapt" ? axa.saptamani : [];
  const grupAles = ales?.fel === "grup" ? ales.cod : null;
  return (
    <>
      <div className="pg-bara">
        <div className="tabs niveluri" role="group" aria-label={t("prog.zoom.titlu")}>
          {(["tot", "luna", "sapt"] as Zoom[]).map((z) => (
            <button key={z} type="button" aria-pressed={zoom === z} className={zoom === z ? "on" : ""}
                    onClick={() => setZoom(z)}>{t(z === "tot" ? "prog.zoom.tot" : z === "luna" ? "prog.zoom.luna" : "prog.zoom.saptamana")}</button>
          ))}
        </div>
        <label className="pg-dot"><input type="checkbox" checked={doarRitm} onChange={(e) => setDoarRitm(e.target.checked)} />
          <Termeni text={t("prog.doar_ritmul")} /></label>
        <span style={{ flex: "1 1 auto" }} />
        <button type="button" className="btn btng"
                onClick={() => setDeschise(toateDeschise ? new Set() : new Set(grupuri.map((g) => g.cod)))}>
          {t(toateDeschise ? "prog.strange_tot" : "prog.desfa_tot")}</button>
      </div>
      {p.avertismente.length > 0 && (
        <div className="pg-avert-bara" role="note">
          <b>{t("prog.avertismente")}</b>
          {p.avertismente.map((a, i) => <span key={i}>{a}</span>)}
        </div>
      )}
      <div className="pg-grafic">
        <div className="pg-cutie" ref={cutie}>
          <div className="pg-panza" style={{ width: LATIME_ETICHETE + latime }}>
            <div className="pg-cap">
              <div className="pg-colt">{t("prog.corp_nivel")}</div>
              <div className="pg-cap-t" style={{ width: latime }}>
                <div className="pg-cap-sus">
                  {sus.map(({ d, text }) => (
                    <span key={+d.data} className="pg-div" style={{ left: `${d.stanga}%`, width: `${d.latime}%` }}>
                      <span className="lipit">{text}</span></span>
                  ))}
                </div>
                <div className="pg-cap-jos">
                  {jos.map(({ d, text }) => (
                    <span key={+d.data} className="pg-div" style={{ left: `${d.stanga}%`, width: `${d.latime}%` }}
                          title={data(d.data.toISOString().slice(0, 10))}>{text}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="pg-corp">
              <div className="pg-grila" aria-hidden="true" style={{ left: LATIME_ETICHETE, width: latime }}>
                {liniiSlabe.map((d) => <span key={`s${+d.data}`} className="slaba" style={{ left: `${d.stanga}%` }} />)}
                {axa.luni.map((d) => <span key={`l${+d.data}`} style={{ left: `${d.stanga}%` }} />)}
                {axa.ani.slice(1).map((d) => <span key={`a${+d.data}`} className="an" style={{ left: `${d.stanga}%` }} />)}
              </div>
              {grupuri.map((g) => {
                const deschis = deschise.has(g.cod);
                return (
                  <div key={g.cod} role="rowgroup">
                    <div className={`pg-r pg-r-grup${grupAles === g.cod ? " ales" : ""}`}>
                      <div className="pg-r-et">
                        <button type="button" className="chev" onClick={() => comuta(g.cod)} aria-expanded={deschis}
                                aria-label={t(deschis ? "prog.strange" : "prog.desfa", { n: g.eticheta })}>{deschis ? "▾" : "▸"}</button>
                        <button type="button" className="nume" onClick={() => setAles(grupAles === g.cod ? null : { fel: "grup", cod: g.cod })}>
                          <b>{g.eticheta}</b>{g.regim && <span className="sub">{g.regim}</span>}
                        </button>
                      </div>
                      <div className="pg-r-t" style={{ width: latime }}>
                        {/* tot ce face bara face și numele din stânga: pentru tastatură și cititor rămâne numele */}
                        <button type="button" className={`pg-rezumat${deschis ? " deschis" : ""}`}
                                style={{ left: `${g.stanga}%`, width: `${g.latime}%` }} aria-hidden="true" tabIndex={-1}
                                onClick={() => setAles(grupAles === g.cod ? null : { fel: "grup", cod: g.cod })}>
                          {g.structura && (
                            <span style={{ left: `${((g.structura.stanga - g.stanga) / (g.latime || 1)) * 100}%`,
                                           width: `${(g.structura.latime / (g.latime || 1)) * 100}%`, background: culoareEtapei("E30") }} />
                          )}
                        </button>
                      </div>
                    </div>
                    {deschis && g.randuri.map((r) => (
                      <div key={r.cheie} className={`pg-r${etapaAleasa && r.segmente.some((s) => s.e === etapaAleasa) ? " ales" : ""}`}>
                        <div className="pg-r-et" title={r.eticheta}><span className="nivel">{r.eticheta}</span></div>
                        <div className="pg-r-t" style={{ width: latime }}>
                          {r.segmente.map((s) => {
                            const e = s.e;
                            const esteAles = etapaAleasa === e;
                            const stins = doarRitm && !["E10", "E20", "E30"].includes(s.etapa);
                            // sub-fazele pachetului în bara lui (cofrarea deschisă, armarea medie,
                            // turnarea plină; placa începe cu un despărțitor), fiecare cu numele și
                            // datele ei la hover; bara întreagă le înșiră și în titlul ei
                            const faze = subSegmente(e);
                            const titlu = [`${s.nume} · ${data(e.start)} – ${data(e.sfarsit)}`,
                                           ...((e.faze ?? []).length > 1 ? (e.faze ?? []).map((f) => fazaCuDate(t, f)) : [])].join("\n");
                            return (
                              <button key={`${s.etapa}${s.stanga}`} type="button"
                                      className={`pg-seg${esteAles ? " ales" : ""}${stins ? " stins" : ""}`}
                                      style={{ left: `${s.stanga}%`, width: `${s.latime}%`, background: s.culoare }}
                                      aria-label={`${s.nume}, ${s.corp} ${r.eticheta}, ${data(e.start)} – ${data(e.sfarsit)}`}
                                      title={titlu}
                                      onClick={() => setAles(esteAles ? null : { fel: "etapa", e })}>
                                {faze.map((x) => (
                                  <span key={`${x.f.parte ?? ""}|${x.f.faza}|${x.f.de_la}`}
                                        className={`pg-faza ${x.ton}${x.despartit ? " despartit" : ""}`}
                                        style={{ left: `${x.stanga}%`, width: `${x.latime}%` }} aria-hidden="true"
                                        title={fazaCuDate(t, x.f)} />
                                ))}
                                {px(s.latime) >= PX_TEXT_PE_BARA && <span className="pg-seg-nume">{s.nume}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <aside className="pg-detaliu" aria-live="polite">
          {etapaAleasa ? <DetaliuEtapa p={p} e={etapaAleasa} inchide={() => setAles(null)} />
            : grupAles === "S" ? <DetaliuSantier p={p} inchide={() => setAles(null)} />
            : grupAles ? <DetaliuCorp p={p} g={grupuri.find((g) => g.cod === grupAles)!} inchide={() => setAles(null)} />
            : <p className="pg-gol">{t("prog.detaliu.gol")}</p>}
        </aside>
      </div>
      <Etape p={p} />
    </>
  );
}

/** Capul panoului de detaliu: ce e ales, unde, și butonul care-l închide. */
function CapDetaliu({ titlu, sub, culoare, inchide }: { titlu: string; sub?: string; culoare?: string; inchide: () => void }) {
  const t = useT();
  return (
    <div className="pg-det-cap">
      <div>
        <div className="pg-det-titlu">{culoare && <i style={{ background: culoare }} />}{titlu}</div>
        {sub && <div className="bc-um">{sub}</div>}
      </div>
      <button type="button" className="pg-x" onClick={inchide} aria-label={t("prog.detaliu.inchide")}>×</button>
    </div>
  );
}

const muncitoriText = (t: ReturnType<typeof useT>, m: Record<string, number>) =>
  Object.entries(m).map(([k, n]) => `${n} ${t(`prog.m.${k}` as Cheie)}`).join(", ") || "—";

/** Numele unei sub-faze pe ecran: faza și, pe structura turnată, turnarea ei
 *  («cofrare · verticale (stâlpi, pereți)»), cu «în paralel» pe cea suprapusă. */
const numeFazei = (t: ReturnType<typeof useT>, f: FazaPachet) =>
  `${t(`prog.faza.${f.faza}` as Cheie)}${f.parte ? ` · ${t(`prog.parte.${f.parte}` as Cheie)}` : ""}` +
  (f.suprapus ? ` (${t("prog.detaliu.faza_suprapusa")})` : "");

const fazaCuDate = (t: ReturnType<typeof useT>, f: FazaPachet) =>
  f.start && f.sfarsit ? `${numeFazei(t, f)}: ${data(f.start)} – ${data(f.sfarsit)}` : numeFazei(t, f);

function DetaliuEtapa({ p, e, inchide }: { p: Program; e: EtapaProgram; inchide: () => void }) {
  const t = useT();
  const nivel = e.nivel != null ? [...(p.subsoluri ?? []), ...p.corpuri].find((c) => c.cod === e.corp)
    ?.niveluri.find((n) => n.nivel === e.nivel) ?? null : null;
  const unde = e.nivel == null ? (e.corp === "S" ? t("prog.santier") : e.corp)
    : `${e.corp} · ${nivelInCorp(e.corp, numeleNivelului({ nivel: e.nivel, nume: nivel?.nume ?? null }))}`;
  return (
    <>
      <CapDetaliu titlu={e.nume} sub={unde} culoare={culoareEtapei(e.etapa)} inchide={inchide} />
      <dl>
        <dt>{t("prog.detaliu.interval")}</dt><dd className="mono">{data(e.start)} → {data(e.sfarsit)}</dd>
        <dt>{t("prog.kpi.durata")}</dt><dd>{t("prog.kpi.zile_lucratoare", { n: nr(e.zile, 1) })}</dd>
        <dt>{t("prog.detaliu.muncitori")}</dt><dd>{muncitoriText(t, e.muncitori)}</dd>
        <dt>{t("prog.etape.th.ore")}</dt>
        <dd className="mono">{t("prog.detaliu.ore", { n: nr(Object.values(e.ore).reduce((a, b) => a + b, 0)) })}
          {e.ore_macara > 0 ? ` · ${t("prog.detaliu.ore_macara", { n: nr(e.ore_macara) })}` : ""}</dd>
        {nivel?.ciclu && (e.etapa === "E30" || e.etapa === "E20") && (
          <>
            <dt>{t("prog.detaliu.ciclu")}</dt>
            <dd>{t("prog.detaliu.ciclu_text", { n: nr(nivel.ciclu.zile, 1), de: t(`prog.ritm.${nivel.ciclu.da_ritmul}`) })}</dd>
          </>
        )}
        {nivel?.plafon_oameni != null && (
          <>
            <dt>{t("prog.detaliu.plafon")}</dt>
            <dd>{t("prog.detaliu.plafon_text", { n: nr(nivel.plafon_oameni), a: nr(nivel.arie_m2) })}</dd>
          </>
        )}
        {e.impins_zile >= 2 && <dd className="pg-avert">{t("prog.detaliu.impins", { n: nr(e.impins_zile) })}</dd>}
        {(e.faze ?? []).length > 1 && (
          <>
            <dt>{t("prog.detaliu.faze")}</dt>
            <dd>
              <ul className="pg-faze">
                {(e.faze ?? []).map((f, i) => (
                  <li key={i}>
                    <i className={`pg-faza-cip ${TONUL_FAZEI[f.faza]}`} style={{ background: culoareEtapei(e.etapa) }} aria-hidden="true" />
                    {numeFazei(t, f)}
                    {f.start && f.sfarsit && <span className="mono bc-um"> {data(f.start)} – {data(f.sfarsit)}</span>}
                  </li>
                ))}
              </ul>
            </dd>
          </>
        )}
        {e.articole.length > 0 && (
          <>
            <dt>{t("prog.detaliu.articole")}</dt><dd className="mono pg-articole">{e.articole.join(", ")}</dd>
          </>
        )}
      </dl>
    </>
  );
}

/** Un corp întreg (sau un subsol comun): cât ține, structura lui, macaralele
 *  și, dacă a stat după macarale libere sau după subsolul de sub el, cât, sub
 *  ce plafon și după cine; la un subsol, blocurile care stau pe el. */
function DetaliuCorp({ p, g, inchide }: { p: Program; g: GrupGantt; inchide: () => void }) {
  const t = useT();
  const c = [...(p.subsoluri ?? []), ...p.corpuri].find((x) => x.cod === g.cod);
  const toate = g.randuri.flatMap((r) => r.segmente.map((s) => s.e));
  const start = toate.reduce((a, e) => (e.start < a ? e.start : a), toate[0]?.start ?? p.start);
  const sfarsit = toate.reduce((a, e) => (e.sfarsit > a ? e.sfarsit : a), toate[0]?.sfarsit ?? p.sfarsit);
  const mac = g.macarale;
  // `macarale.subsol` e numai la blocul ținut de subsol; cel ținut de ordine stă tot pe el
  const peSubsol = mac?.subsol ?? p.subsoluri?.find((s) => s.corpuri.includes(g.cod))?.cod;
  const nivelurile = c?.niveluri.length ?? 0;
  const sub = [g.regim, g.amprenta_m2 != null ? t("prog.corp.amprenta", { a: nr(g.amprenta_m2) }) : null].filter(Boolean).join(" · ");
  return (
    <>
      <CapDetaliu titlu={g.eticheta} sub={sub} inchide={inchide} />
      <dl>
        <dt>{t("prog.detaliu.interval")}</dt><dd className="mono">{data(start)} → {data(sfarsit)}</dd>
        {g.pe_el && g.pe_el.length > 0 && (
          <>
            <dt>{t("prog.subsol.pe_el")}</dt>
            <dd>{g.pe_el.join(", ")}<div className="bc-um">{t("prog.subsol.pe_el_sub")}</div></dd>
          </>
        )}
        {peSubsol && (
          <>
            <dt>{t("prog.corp.pe_subsol")}</dt>
            <dd>{t("prog.corp.pe_subsol_text", { s: peSubsol,
                                                 d: data(p.macarale.pe_subsol?.[peSubsol]?.sfarsit ?? start) })}</dd>
          </>
        )}
        {mac?.structura && (
          <>
            <dt>{t("prog.lant.structura")}</dt>
            <dd><span className="mono">{data(mac.structura.start)} → {data(mac.structura.sfarsit)}</span>
              {" · "}{t("prog.lant.niveluri", { n: nr(nivelurile) })}</dd>
          </>
        )}
        {mac && (
          <>
            <dt>{t("prog.macarale.titlu")}</dt>
            <dd>{t("prog.corp.macarale", { b: nr(mac.buc), l: nr(mac.luni, 1) })}
              <div className="bc-um mono">{data(mac.start)} → {data(mac.sfarsit)}</div></dd>
            {mac.asteptat_zile >= 1 && (
              <>
                <dt>{t("prog.corp.asteptat")}</dt>
                <dd className="pg-avert">{t(mac.eliberat_de ? "prog.corp.asteptat_text" : "prog.corp.asteptat_text_fara", {
                  n: nr(mac.asteptat_zile), p: nr(mac.plafon_la_start ?? p.macarale.plafon),
                  d: data(mac.structura?.start ?? mac.start), c: mac.eliberat_de ?? "" })}</dd>
              </>
            )}
          </>
        )}
      </dl>
    </>
  );
}

/** Șantierul: lucrările lui pe rând și cum s-au așezat corpurile sub plafonul
 *  de macarale; așteptarea fiecărui corp stă în detaliul corpului. */
function DetaliuSantier({ p, inchide }: { p: Program; inchide: () => void }) {
  const t = useT();
  const generale = (p.explicatii ?? []).filter((x) => ![...(p.subsoluri ?? []), ...p.corpuri].some((c) => x.startsWith(`${c.cod}:`)));
  return (
    <>
      <CapDetaliu titlu={t("prog.santier")} inchide={inchide} />
      <dl>
        <dt>{t("prog.detaliu.interval")}</dt><dd className="mono">{data(p.start)} → {data(p.sfarsit)}</dd>
        <dt>{t("prog.corp.lucrari")}</dt>
        {p.santier.map((e) => (
          <dd key={`${e.etapa}${e.nivel ?? ""}`} className="pg-det-rand">
            <span className="pg-dot"><i style={{ background: culoareEtapei(e.etapa) }} />{e.nume}</span>
            <div className="mono bc-um">{data(e.start)} → {data(e.sfarsit)}</div>
          </dd>
        ))}
        {generale.length > 0 && <dt>{t("prog.explicatii")}</dt>}
        {generale.map((x, i) => <dd key={i} className="bc-um"><Termeni text={x} /></dd>)}
      </dl>
    </>
  );
}

function Etape({ p }: { p: Program }) {
  const t = useT();
  const nume = new Map(p.etape.map((e) => [e.cod, e.nume]));
  return (
    <section className="ip-card" aria-labelledby="pg-etape-t">
      <div className="ip-card-h"><h2 id="pg-etape-t">{t("prog.etape.titlu")}</h2></div>
      <div className="bc-scroll">
        <table className="sc-tab">
          <thead><tr>
            <th>{t("prog.etape.th.etapa")}</th><th>{t("prog.etape.th.inceput")}</th><th>{t("prog.etape.th.sfarsit")}</th>
            <th className="bc-n">{t("prog.etape.th.unitati")}</th><th>{t("prog.etape.th.muncitori")}</th>
            <th className="bc-n"><Termeni text={t("prog.etape.th.ore")} /></th><th>{t("prog.etape.th.decalaj")}</th>
          </tr></thead>
          <tbody>
            {p.etape.map((e) => (
              <tr key={e.cod}>
                <td><span className="pg-dot"><i style={{ background: culoareEtapei(e.cod) }} /><b>{e.nume}</b></span>
                  {e.ritm && <span className="ip-pill ip-pill-acc" style={{ marginLeft: 6 }}>{t("prog.etape.da_ritmul")}</span>}</td>
                <td className="mono">{data(e.start)}</td>
                <td className="mono">{data(e.sfarsit)}</td>
                <td className="bc-n mono">{e.unitati}</td>
                <td>{Object.entries(e.muncitori).map(([m, n]) => `${n} ${t(`prog.m.${m}` as Cheie)}`).join(", ") || "—"}</td>
                <td className="bc-n mono">{nr(e.ore)}</td>
                <td className="bc-um">{e.ritm || e.decalaj_final_zile == null ? "—"
                  : t("prog.etape.decalaj_text", { n: nr(e.decalaj_final_zile), de: e.dupa.map((d) => nume.get(d) ?? d).join(", ") })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// --- resursele ----------------------------------------------------------------

function Resurse({ p, sc, potScrie }: { p: Program; sc: Scenarii; potScrie: boolean }) {
  const t = useT();
  const meserii = Object.entries(p.meserii).sort((a, b) => b[1].ore - a[1].ore);
  const recomandat = Object.fromEntries(meserii.map(([m, x]) => [m, x.recomandat]));
  const [folosit, setFolosit] = useState<Record<string, number>>(() => Object.fromEntries(meserii.map(([m, x]) => [m, x.folosit])));
  useEffect(() => { setFolosit(Object.fromEntries(meserii.map(([m, x]) => [m, x.folosit]))); }, [p]); // eslint-disable-line react-hooks/exhaustive-deps
  const schimbat = meserii.some(([m, x]) => folosit[m] !== x.folosit);
  const bare = useMemo(() => bareHistograma(p, ORDINEA_ETAPELOR), [p]);
  const etapeDinProgram = ORDINEA_ETAPELOR.filter((e) => p.histograma.some((s) => s.pe_etapa[e]));
  const reprogrameaza = () => {
    const program = cuMuncitorii(sc.curent.program, folosit, recomandat);
    void sc.creeaza({}, { nume: t("prog.scenariu_resurse"), autor: "om", nota: t("prog.scenariu_nota"), program })
      .catch(() => {});
  };
  const sfarsitul = p.histograma[p.histograma.length - 1]?.start;
  return (
    <>
      {potScrie && (
        <div className="pg-bara">
          <span style={{ flex: "1 1 auto" }} />
          <button type="button" className="btn" disabled={!schimbat}
                  onClick={() => setFolosit(Object.fromEntries(meserii.map(([m, x]) => [m, x.recomandat])))}>
            {t("prog.revino")}</button>
          <button type="button" className="btn" disabled={!schimbat || sc.seSalveaza} onClick={reprogrameaza}>
            {t("prog.reprogrameaza")}</button>
        </div>
      )}
      <section className="ip-card" aria-labelledby="pg-hist-t">
        <div className="ip-card-h"><h2 id="pg-hist-t">{t("prog.hist.titlu")}</h2>
          <span className="bc-um">{t("prog.hist.sub", { v: nr(p.muncitori_varf), m: nr(p.muncitori_medie), s: nr(p.histograma.length) })}</span>
          <span style={{ flex: "1 1 auto" }} />
          <span className="pg-legenda">
            {etapeDinProgram.map((e) => (
              <span key={e} className="pg-dot"><i style={{ background: culoareEtapei(e) }} />{p.etape.find((x) => x.cod === e)?.nume}</span>
            ))}
          </span>
        </div>
        <div className="pg-hist" role="img" aria-label={t("prog.hist.titlu")}>
          {bare.map((b, i) => (
            <div key={i} className="pg-bar" title={t("prog.hist.saptamana", { n: i + 1, d: data(b.start), t: nr(b.total) })}>
              {b.parti.map((x) => <span key={x.etapa} style={{ height: `${x.h}%`, background: x.culoare }} />)}
            </div>
          ))}
        </div>
        <div className="pg-hist-axa"><span>{data(p.start)}</span><span>{sfarsitul ? data(sfarsitul) : ""}</span></div>
      </section>
      <div className="pg-doua">
        <section className="ip-card" aria-labelledby="pg-mes-t">
          <div className="ip-card-h"><h2 id="pg-mes-t">{t("prog.meserii.titlu")}</h2>
            <Lamurire text={t("prog.resurse.intro")} />
            <span className="bc-um">{t("prog.meserii.sub", {
              z: (p.setari.zile_lucratoare ?? [0, 1, 2, 3, 4]).map((z) => t(ZILE[z])).join("–"),
              h: nr(p.setari.ore_pe_zi ?? 8), p: nr(p.setari.productivitate ?? 1, 2) })}</span></div>
          <div className="bc-scroll">
            <table className="sc-tab">
              <thead><tr>
                <th>{t("prog.meserii.th.categorie")}</th><th className="bc-n">{t("prog.meserii.th.recomandat")}</th>
                <th className="bc-n">{t("prog.meserii.th.folosit")}</th><th className="bc-n">{t("prog.meserii.th.varf")}</th>
                <th className="bc-n"><Termeni text={t("prog.meserii.th.ore")} /></th><th className="bc-n">{t("prog.meserii.th.zile")}</th>
                <th>{t("prog.meserii.th.utilizare")}</th><th>{t("prog.meserii.th.stare")}</th>
              </tr></thead>
              <tbody>
                {meserii.map(([m, x]) => {
                  const impusa = (sc.curent.program?.muncitori?.[m] ?? null) != null || folosit[m] !== x.recomandat;
                  const stare = stareaMeseriei(x, impusa);
                  return (
                    <tr key={m}>
                      <td><b>{t(`prog.m.${m}` as Cheie)}</b>
                        <div className="bc-um">{x.etape.map((e) => p.etape.find((y) => y.cod === e)?.nume ?? e).join(", ")}</div></td>
                      <td className="bc-n mono">{x.recomandat}</td>
                      <td className="bc-n">
                        {potScrie ? (
                          <input type="number" min={0} className={`pg-n${folosit[m] !== x.recomandat ? " schimbat" : ""}`}
                                 value={folosit[m] ?? x.folosit} aria-label={`${t("prog.meserii.th.folosit")} ${t(`prog.m.${m}` as Cheie)}`}
                                 onChange={(e) => setFolosit({ ...folosit, [m]: Math.max(0, Math.round(Number(e.target.value) || 0)) })} />
                        ) : <span className="mono">{x.folosit}</span>}
                      </td>
                      <td className="bc-n mono">{nr(x.varf)}</td>
                      <td className="bc-n mono">{nr(x.ore)}</td>
                      <td className="bc-n mono">{nr(x.zile_pe_santier)}</td>
                      <td><span className="pg-util"><span style={{ width: `${Math.min(x.utilizare_pct ?? 0, 100)}%` }} /></span>
                        {" "}<span className="mono" style={{ fontSize: "var(--fs-mic)" }}>{x.utilizare_pct == null ? "—" : `${nr(x.utilizare_pct)} %`}</span></td>
                      <td><span className={`ip-pill ${stare === "la_ritm" ? "ip-pill-ok" : stare === "impusa" ? "ip-pill-acc" : stare === "standby" ? "ip-pill-amb" : "ip-pill-mut"}`}>
                        {t(`prog.stare.${stare}`)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
        <section className="ip-card" aria-labelledby="pg-mac-t" style={{ flex: "1 1 300px", maxWidth: 460 }}>
          <div className="ip-card-h"><h2 id="pg-mac-t">{t("prog.macarale.titlu")}</h2><Lamurire text={t("prog.macarale.sub")} /></div>
          <table className="sc-tab">
            <thead><tr><th>{t("prog.macarale.th.corp")}</th><th className="bc-n">{t("prog.macarale.th.buc")}</th>
              <th>{t("prog.macarale.th.structura")}</th><th>{t("prog.macarale.th.pe_santier")}</th>
              <th className="bc-n">{t("prog.macarale.th.luni")}</th><th>{t("prog.macarale.th.asteptat")}</th></tr></thead>
            <tbody>
              {[...Object.entries(p.macarale.pe_subsol ?? {}), ...Object.entries(p.macarale.pe_corp)].map(([corp, m]) => (
                <tr key={corp}><td><b>{corp}</b></td><td className="bc-n mono">{m.buc}</td>
                  <td className="mono" style={{ fontSize: "var(--fs-mic)" }}>{m.structura ? `${data(m.structura.start)} → ${data(m.structura.sfarsit)}` : "—"}</td>
                  <td className="mono" style={{ fontSize: "var(--fs-mic)" }}>{data(m.start)} → {data(m.sfarsit)}</td>
                  <td className="bc-n mono">{nr(m.luni_macara, 1)}</td>
                  <td className="bc-um">{m.asteptat_zile >= 1
                    ? (m.eliberat_de ? t("prog.macarale.asteptat", { n: nr(m.asteptat_zile), c: m.eliberat_de })
                      : t("prog.macarale.asteptat_fara", { n: nr(m.asteptat_zile) }))
                    : "—"}</td></tr>
              ))}
              <tr><td><b>{t("prog.macarale.total")}</b>
                <div className="bc-um">{t("prog.macarale.plafon", { n: p.macarale.max_in_structura, m: p.macarale.max_simultan, p: p.macarale.plafon })}</div>
                {Object.keys(p.macarale.pe_santier ?? {}).length > 0 && (
                  <div className="bc-um">{t("prog.macarale.santier", { n: nr(Math.max(...Object.values(p.macarale.pe_santier))) })}</div>
                )}</td>
                <td className="bc-n mono">{Object.values(p.macarale.pe_corp).reduce((a, m) => a + m.buc, 0)}</td><td /><td />
                <td className="bc-n mono"><b>{nr(p.macarale.luni_macara, 1)}</b></td><td /></tr>
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
}

// --- ținta: optimizatorul ------------------------------------------------------

/** «Cu atâtea luni mai repede, cât mai ieftin» sau «cel mai bun ROI anual»:
 *  optimizatorul (serviciul de planificare, pe motor) încearcă pârghiile pas
 *  cu pas și întoarce drumul întreg — omul vede fiecare pas cu ce a câștigat și
 *  ce a costat, refuzurile cu motiv, și face un scenariu din plan. */
function Tinta({ p, projectId, solutionId, scenariu, sc, potScrie }: {
  p: Program; projectId: string; solutionId: string; scenariu: string | null; sc: Scenarii; potScrie: boolean;
}) {
  const t = useT();
  const [mod, setMod] = useState<"luni" | "roi">("luni");
  const [luni, setLuni] = useState<number>(() => Math.max(1, Math.round((p.luni - 3) * 10) / 10));
  const [rez, setRez] = useState<RezultatOptimizare | null>(null);
  const [eroare, setEroare] = useState<string | null>(null);
  const [cauta, setCauta] = useState(false);
  const porneste = async () => {
    setCauta(true);
    setEroare(null);
    try {
      setRez(await optimizeazaProgram(projectId, solutionId, { tinta: mod === "luni" ? { mod, luni } : { mod }, scenariu }));
    } catch (e) {
      setRez(null);
      setEroare(e instanceof ApiError ? e.message : String(e));
    } finally {
      setCauta(false);
    }
  };
  const numeParghiei = (id: string) => (rez?.parghii ?? p.parghii ?? []).find((x) => x.id === id)?.nume ?? id;
  const scenariuDinPlan = () => {
    if (!rez) return;
    const program: SetariProgram = { ...(sc.curent.program ?? {}), parghii: rez.setari.parghii ?? [] };
    const nume = rez.mod === "luni" ? t("prog.tinta.scenariu_luni", { n: nr(rez.rezultat.luni, 1) }) : t("prog.tinta.scenariu_roi");
    void sc.creeaza({}, { nume, autor: "om", program,
                          nota: t("prog.tinta.scenariu_nota", { p: rez.pasi.map((x) => numeParghiei(x.parghie)).join(", ") || "—" }) })
      .catch(() => {});
  };
  const pc = (v: number | null | undefined) => (v == null ? "—" : `${nr(v * 100, 2)} %`);
  return (
    <>
      <section className="ip-card" aria-labelledby="pg-tinta-t">
        <div className="ip-card-h"><h2 id="pg-tinta-t">{t("prog.tinta.titlu")}</h2>
          <Lamurire text={t("prog.tinta.intro")} />
          <span className="bc-um">{t("prog.tinta.sub", { n: nr(p.luni, 1) })}</span></div>
        <div className="pg-tinta-forma">
          <label className="pg-dot"><input type="radio" name="pg-mod" checked={mod === "luni"} onChange={() => setMod("luni")} />
            {t("prog.tinta.mod_luni")}</label>
          <input type="number" min={1} step={0.5} className="pg-n" value={luni} disabled={mod !== "luni"} aria-label={t("prog.tinta.luni")}
                 onChange={(e) => setLuni(Math.max(1, Number(e.target.value) || 1))} />
          <span className="bc-um">{t("prog.tinta.luni")}</span>
          <label className="pg-dot" style={{ marginLeft: 12 }}><input type="radio" name="pg-mod" checked={mod === "roi"} onChange={() => setMod("roi")} />
            <Termeni text={t("prog.tinta.mod_roi")} /></label>
          <span style={{ flex: "1 1 auto" }} />
          <button type="button" className="btn" disabled={cauta} onClick={() => void porneste()}>
            {cauta ? t("prog.tinta.cauta_acum") : t("prog.tinta.cauta")}</button>
        </div>
        {eroare && <span className="ip-err">{t("prog.eroare", { m: eroare })}</span>}
        {cauta && !rez && <SeIncarca randuri={4} />}
      </section>
      {rez && (
        <>
          <div className="pg-kpi">
            <div><div className="l">{t("prog.kpi.durata")}</div>
              <div className="v">{t("prog.tinta.de_la_la", { a: nr(rez.baza.luni, 1), b: nr(rez.rezultat.luni, 1) })}</div>
              <div className="s">{rez.atins === true ? t("prog.tinta.atins") : rez.atins === false ? t("prog.tinta.neatins", { n: nr(rez.tinta_luni ?? 0, 1) }) : t("prog.tinta.cel_mai_bun")}</div></div>
            {rez.baza.roi_anual != null && (
              <div><div className="l"><Termeni text={t("prog.tinta.roi_an")} /></div>
                <div className="v">{pc(rez.baza.roi_anual)} → {pc(rez.rezultat.roi_anual)}</div>
                <div className="s">{rez.rezultat.tic != null && rez.baza.tic != null ? t("prog.tinta.tic", { a: nr(rez.baza.tic), b: nr(rez.rezultat.tic) }) : ""}</div></div>
            )}
            <div><div className="l">{t("prog.tinta.cost_extra")}</div>
              <div className="v mono">{nr(rez.rezultat.cost_extra_eur)} €</div>
              <div className="s">{t("prog.tinta.cost_extra_sub", { s: nr(rez.rezultat.cost_suplimentar_eur), m: nr(rez.rezultat.cost_macarale_eur - rez.baza.cost_macarale_eur) })}</div></div>
            <div><div className="l">{t("prog.tinta.pasi")}</div><div className="v">{rez.pasi.length}</div>
              <div className="s">{rez.restrictii_atinse.length ? t("prog.kpi.restrictii_atinse", { n: nr(rez.restrictii_atinse.length) }) : ""}</div></div>
          </div>
          <div className="pg-doua">
            <section className="ip-card" aria-labelledby="pg-plan-t">
              <div className="ip-card-h"><h2 id="pg-plan-t">{t("prog.tinta.plan")}</h2>
                <Lamurire text={t("prog.tinta.plan_sub")} />
                <span style={{ flex: "1 1 auto" }} />
                {potScrie && rez.pasi.length > 0 && (
                  <button type="button" className="btn" disabled={sc.seSalveaza} onClick={scenariuDinPlan}>{t("prog.tinta.scenariu")}</button>
                )}
              </div>
              {rez.pasi.length === 0 ? <span className="bc-um">{t("prog.tinta.niciun_pas")}</span> : (
                <table className="sc-tab">
                  <thead><tr><th>#</th><th>{t("prog.tinta.th.parghie")}</th><th className="bc-n">{t("prog.tinta.th.luni")}</th>
                    <th className="bc-n">{t("prog.tinta.th.cost")}</th><th className="bc-n"><Termeni text={t("prog.tinta.th.roi")} /></th>
                    <th className="bc-n">{t("prog.tinta.th.dupa")}</th></tr></thead>
                  <tbody>
                    {rez.traiectorie.slice(1).map((x) => (
                      <tr key={x.pas}><td className="mono bc-um">{x.pas}</td>
                        <td><b>{numeParghiei(x.parghie ?? "")}</b>{x.valoare != null && x.parghie === "macarale_plus" ? ` +${nr(x.valoare)}` : ""}</td>
                        <td className="bc-n mono">−{nr(x.d_luni ?? 0, 1)}</td>
                        <td className="bc-n mono">{(x.d_cost ?? 0) >= 0 ? "+" : "−"}{nr(Math.abs(x.d_cost ?? 0))} €</td>
                        <td className="bc-n mono">{x.d_roi != null ? `${x.d_roi >= 0 ? "+" : "−"}${nr(Math.abs(x.d_roi) * 100, 2)} pp` : "—"}</td>
                        <td className="bc-n mono">{nr(x.luni, 1)}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}
              {rez.costuri_suplimentare.length > 0 && (
                <ul className="pg-reguli pg-de-ce">
                  {rez.costuri_suplimentare.map((c) => <li key={c.id} className="bc-um"><Termeni text={c.explicatie} /></li>)}
                </ul>
              )}
            </section>
            <section className="ip-card" aria-labelledby="pg-ref-t">
              <div className="ip-card-h"><h2 id="pg-ref-t">{t("prog.tinta.refuzate")}</h2>
                <Lamurire text={t("prog.tinta.refuzate_sub")} /></div>
              {rez.refuzate.length === 0 && rez.fara_castig.length === 0 ? <span className="bc-um">{t("prog.tinta.nimic_refuzat")}</span> : (
                <ul className="pg-reguli">
                  {rez.refuzate.map((x) => (
                    <li key={x.id}><div><b>{x.nume}</b> <span className="ip-pill ip-pill-mut">{t("prog.parghii.refuzata")}</span></div>
                      <div className="bc-um"><Termeni text={x.motiv} /></div></li>
                  ))}
                  {rez.fara_castig.map((x) => (
                    <li key={x.id}><div><b>{numeParghiei(x.id)}</b> <span className="ip-pill ip-pill-amb">{t("prog.tinta.fara_castig")}</span></div>
                      <div className="bc-um">{t("prog.tinta.fara_castig_sub", { l: nr(x.d_luni, 1), c: nr(x.d_cost), r: nr(x.d_roi * 100, 2) })}</div></li>
                  ))}
                </ul>
              )}
              {rez.restrictii.filter((r) => r.atinsa).length > 0 && (
                <div className="pg-de-ce">
                  <b>{t("prog.kpi.restrictii")}</b>
                  <ul className="pg-reguli">
                    {rez.restrictii.filter((r) => r.atinsa).map((r) => (
                      <li key={r.id}><div><b>{r.nume}</b></div><div className="bc-um"><Termeni text={r.atinsa_text ?? r.explicatie} /></div></li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </>
  );
}

// --- regulile și calendarul -----------------------------------------------------

type Numerica = "ciclu_min_zl" | "ciclu_min_subsol_zl" | "marja_zl" | "productivitate" | "ore_macara_m3"
  | "amprenta_pe_macara_m2" | "macarale_max_santier" | "m2_pe_macara_zi" | "densitate_m2_om" | "min_om_nivel"
  | "beton_statie_m3_zi" | "brat_macara_m" | "lift_de_la_etaj";
type Bifa = "pompa_beton" | "cofraje_rapide" | "lift_santier";

function Reguli({ projectId, setari, potScrie, dupaSalvare, reguli }: {
  projectId: string; setari: SetariProiect | null; potScrie: boolean; dupaSalvare: () => void;
  /** regulile cu parametrii efectivi și explicațiile lor (ale programului curent) */
  reguli: RegulaProgram[] | null;
}) {
  const t = useT();
  const [form, setForm] = useState<SetariProgram>({});
  const [mesaj, setMesaj] = useState<string | null>(null);
  const [seSalveaza, setSeSalveaza] = useState(false);
  useEffect(() => { setForm(setari?.data ?? {}); }, [setari]);
  if (!setari) return <SeIncarca randuri={6} />;
  const impl: ImpliciteProgram = setari.implicite;
  const val = <K extends keyof SetariProgram>(k: K) => (form[k] ?? impl.setari[k]) as SetariProgram[K];
  const pune = <K extends keyof SetariProgram>(k: K, v: SetariProgram[K] | null) => {
    const f = { ...form };
    if (v == null || JSON.stringify(v) === JSON.stringify(impl.setari[k])) delete f[k]; else f[k] = v;
    setForm(f);
  };
  const lag = (e: EtapaCatalog) => form.lag_zile?.[e.cod] ?? e.lag_zile;
  const puneLag = (e: EtapaCatalog, v: number) => {
    const l = { ...(form.lag_zile ?? {}) };
    if (v === e.lag_zile) delete l[e.cod]; else l[e.cod] = v;
    pune("lag_zile", Object.keys(l).length ? l : null);
  };
  const zile = val("zile_lucratoare") ?? [0, 1, 2, 3, 4];
  const libere = (val("libere") ?? []) as (string | [string, string])[];
  const perioade = (val("macarale_perioade") ?? []) as PerioadaMacarale[];
  const punePerioade = (l: PerioadaMacarale[]) => pune("macarale_perioade", l.length ? l : null);
  const salveaza = async () => {
    setSeSalveaza(true);
    setMesaj(null);
    try {
      await putSetariProgram(projectId, setari.rev, form);
      setMesaj(t("prog.salvat"));
      dupaSalvare();
    } catch (e) {
      setMesaj(e instanceof ApiError && e.status === 409 ? t("prog.rev_vechi") : String(e));
      if (e instanceof ApiError && e.status === 409) dupaSalvare();
    } finally {
      setSeSalveaza(false);
    }
  };
  const bifa = (k: Bifa, nume: Cheie, sub: Cheie, eticheta: Cheie) => (
    <div className="pg-rand-set" key={k}>
      <div className="nm">{t(nume)}<Lamurire text={t(sub)} /></div>
      <div><label className="pg-dot"><input type="checkbox" checked={!!(val(k) ?? false)} disabled={!potScrie}
                                           onChange={(e) => pune(k, e.target.checked)} />{t(eticheta)}</label></div>
    </div>
  );
  const numerica = (k: Numerica, nume: Cheie, sub: Cheie, um: Cheie | null, pas = 1) => (
    <div className="pg-rand-set" key={k}>
      <div className="nm">{t(nume)}<Lamurire text={t(sub)} /></div>
      <div><input type="number" className={`pg-n${form[k] != null ? " schimbat" : ""}`} step={pas} disabled={!potScrie}
                  value={val(k) ?? ""} aria-label={t(nume)}
                  onChange={(e) => pune(k, e.target.value === "" ? null : Number(e.target.value))} />
        {um && <span className="bc-um"> {t(um)}</span>}</div>
    </div>
  );
  return (
    <>
      {(mesaj || potScrie) && (
        <div className="pg-bara">
          <span style={{ flex: "1 1 auto" }} />
          {mesaj && <span className="pg-mesaj">{mesaj}</span>}
          {potScrie && (
            <>
              <button type="button" className="btn btng" onClick={() => setForm({})}>{t("prog.revino_global")}</button>
              <button type="button" className="btn" disabled={seSalveaza} onClick={() => void salveaza()}>{t("prog.salveaza")}</button>
            </>
          )}
        </div>
      )}
      <section className="ip-card" aria-labelledby="pg-reg-t">
        <div className="ip-card-h"><h2 id="pg-reg-t">{t("prog.reguli.titlu")}</h2>
          <Lamurire text={`${t("prog.reguli.intro")} ${t("prog.reguli.sub")}`} /></div>
        <div className="bc-scroll">
          <table className="sc-tab">
            <thead><tr><th>{t("prog.reguli.th.cod")}</th><th>{t("prog.reguli.th.etapa")}</th><th>{t("prog.reguli.th.unitate")}</th>
              <th>{t("prog.reguli.th.dupa")}</th><th className="bc-n"><Termeni text={t("prog.reguli.th.lag")} /></th></tr></thead>
            <tbody>
              {impl.etape.map((e) => (
                <tr key={e.cod}>
                  <td className="mono bc-um">{e.cod}</td>
                  <td><span className="pg-dot"><i style={{ background: culoareEtapei(e.cod) }} /><b>{e.nume}</b></span>
                    {/* de ce stă etapa la distanța asta: regula ei de lag */}
                    {(() => { const r = reguli?.find((x) => x.id === `lag.${e.cod}`); return r ? <Lamurire text={r.explicatie} /> : null; })()}
                    {e.ritm && <span className="ip-pill ip-pill-acc" style={{ marginLeft: 6 }}>{t("prog.etape.da_ritmul")}</span>}
                    {e.zile_fixe && <span className="bc-um"> · {t("prog.kpi.zile_lucratoare", { n: e.zile_fixe })}</span>}</td>
                  <td><span className="ip-pill ip-pill-mut">{t(`prog.unitate.${e.unitate}`)}</span></td>
                  <td className="bc-um">{e.dupa.map(([p, mod]) => `${impl.etape.find((x) => x.cod === p)?.nume ?? p} (${t(`prog.mod.${mod}` as Cheie)})`).join("; ") || "—"}</td>
                  <td className="bc-n">
                    {e.dupa.length ? (
                      <input type="number" min={0} className={`pg-n${form.lag_zile?.[e.cod] != null ? " schimbat" : ""}`}
                             value={lag(e)} disabled={!potScrie} aria-label={`${t("prog.reguli.th.lag")} ${e.nume}`}
                             onChange={(ev) => puneLag(e, Math.max(0, Number(ev.target.value) || 0))} />
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {/* REGULILE ca date, cu parametrii efectivi și de ce există fiecare: ce
          ține programul în frâu și ce poate explica asistentul */}
      {reguli && reguli.some((r) => r.categorie !== "lag") && (
        <section className="ip-card" aria-labelledby="pg-cat-t">
          <div className="ip-card-h"><h2 id="pg-cat-t">{t("prog.reguli.catalog.titlu")}</h2>
            <Lamurire text={t("prog.reguli.catalog.sub")} /></div>
          <ul className="pg-reguli">
            {reguli.filter((r) => r.categorie !== "lag").map((r) => (
              <li key={r.id} className={r.activa ? "" : "inactiva"}>
                <div>
                  <b>{r.nume}</b><Lamurire text={r.explicatie} />
                  <span className="ip-pill ip-pill-mut">{t(`prog.reguli.categorie.${r.categorie}` as Cheie)}</span>
                  {!r.activa && <span className="ip-pill ip-pill-amb">{t("prog.reguli.inactiva")}</span>}
                  {Object.keys(r.parametri).length > 0 && (
                    <span className="mono bc-um"> {Object.entries(r.parametri)
                      .filter(([, v]) => typeof v !== "object" || v === null)
                      .map(([k, v]) => `${k} = ${String(v)}`).join(" · ")}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      <div className="pg-doua">
        <section className="ip-card" aria-labelledby="pg-ritm-t">
          <div className="ip-card-h"><h2 id="pg-ritm-t">{t("prog.ritm.titlu")}</h2></div>
          {numerica("ciclu_min_zl", "prog.set.ciclu_min", "prog.set.ciclu_min_sub", "prog.um.zl")}
          {numerica("ciclu_min_subsol_zl", "prog.set.ciclu_subsol", "prog.set.ciclu_subsol_sub", "prog.um.zl")}
          {numerica("marja_zl", "prog.set.marja", "prog.set.marja_sub", "prog.um.zl")}
          <div className="pg-rand-set">
            <div className="nm">{t("prog.set.material")}<Lamurire text={t("prog.set.material_sub")} /></div>
            <div><select className={`pg-n${form.material_structura != null ? " schimbat" : ""}`} disabled={!potScrie}
                         value={val("material_structura") ?? "beton"} aria-label={t("prog.set.material")}
                         onChange={(e) => pune("material_structura", e.target.value as "beton" | "prefab")}>
              <option value="beton">{t("prog.material.beton")}</option>
              <option value="prefab">{t("prog.material.prefab")}</option>
            </select></div>
          </div>
          {numerica("productivitate", "prog.set.productivitate", "prog.set.productivitate_sub", null, 0.05)}
          {numerica("ore_macara_m3", "prog.set.ore_macara", "prog.set.ore_macara_sub", "prog.um.h_m3", 0.05)}
          {numerica("amprenta_pe_macara_m2", "prog.set.amprenta_macara", "prog.set.amprenta_macara_sub", "prog.um.m2", 50)}
          {numerica("macarale_max_santier", "prog.set.macarale_max", "prog.set.macarale_max_sub", null)}
          <div className="pg-rand-set">
            <div className="nm">{t("prog.perioade.titlu")}<Lamurire text={t("prog.perioade.sub")} /></div>
            <div className="pg-libere">
              {perioade.map((pr, i) => (
                <div key={i}>
                  <span className="bc-um">{t("prog.calendar.de_la")}</span>
                  <input type="date" className="pg-d" value={pr.de_la} disabled={!potScrie} aria-label={t("prog.calendar.de_la")}
                         onChange={(e) => punePerioade(perioade.map((x, j) => j === i
                           ? { ...x, de_la: e.target.value, pana_la: x.pana_la >= e.target.value ? x.pana_la : e.target.value } : x))} />
                  <span className="bc-um">{t("prog.calendar.pana_la")}</span>
                  <input type="date" className="pg-d" value={pr.pana_la} disabled={!potScrie} aria-label={t("prog.calendar.pana_la")}
                         onChange={(e) => punePerioade(perioade.map((x, j) => j === i
                           ? { ...x, pana_la: e.target.value >= x.de_la ? e.target.value : x.de_la } : x))} />
                  <span className="bc-um">{t("prog.perioade.maxim")}</span>
                  <input type="number" min={1} max={24} className="pg-n" value={pr.maxim} disabled={!potScrie} aria-label={t("prog.perioade.maxim")}
                         onChange={(e) => punePerioade(perioade.map((x, j) => j === i
                           ? { ...x, maxim: Math.min(24, Math.max(1, Math.round(Number(e.target.value) || 1))) } : x))} />
                  {potScrie && <button type="button" className="btn btng" aria-label={t("prog.calendar.sterge")}
                                       onClick={() => punePerioade(perioade.filter((_, j) => j !== i))}>×</button>}
                </div>
              ))}
              {potScrie && (
                <button type="button" className="btn" style={{ height: 28 }}
                        onClick={() => { const z = val("start") ?? impl.setari.start;
                          punePerioade([...perioade, { de_la: z, pana_la: z, maxim: (val("macarale_max_santier") ?? 4) + 1 }]); }}>
                  {t("prog.perioade.adauga")}</button>
              )}
            </div>
          </div>
          {numerica("m2_pe_macara_zi", "prog.set.m2_macara", "prog.set.m2_macara_sub", "prog.um.m2_zi", 5)}
          {numerica("densitate_m2_om", "prog.set.densitate", "prog.set.densitate_sub", "prog.um.m2_om")}
          {numerica("min_om_nivel", "prog.set.min_om", "prog.set.min_om_sub", null)}
          {/* restricțiile fizice și pârghiile puse cu mâna: aceleași setări pe care le mișcă pârghiile */}
          {bifa("pompa_beton", "prog.set.pompa", "prog.set.pompa_sub", "prog.set.pompa_da")}
          {numerica("beton_statie_m3_zi", "prog.set.statie", "prog.set.statie_sub", "prog.um.m3_zi", 10)}
          {numerica("brat_macara_m", "prog.set.brat", "prog.set.brat_sub", "prog.um.m", 5)}
          <div className="pg-rand-set">
            <div className="nm">{t("prog.set.schimburi")}<Lamurire text={t("prog.set.schimburi_sub")} /></div>
            <div><select className={`pg-n${form.schimburi_structura != null ? " schimbat" : ""}`} disabled={!potScrie}
                         value={val("schimburi_structura") ?? 1} aria-label={t("prog.set.schimburi")}
                         onChange={(e) => pune("schimburi_structura", Number(e.target.value) === 2 ? 2 : 1)}>
              <option value={1}>{t("prog.schimburi.unu")}</option>
              <option value={2}>{t("prog.schimburi.doua")}</option>
            </select></div>
          </div>
          {bifa("cofraje_rapide", "prog.set.cofraje_rapide", "prog.set.cofraje_rapide_sub", "prog.set.cofraje_rapide_da")}
          {bifa("lift_santier", "prog.set.lift", "prog.set.lift_sub", "prog.set.lift_da")}
          {numerica("lift_de_la_etaj", "prog.set.lift_etaj", "prog.set.lift_etaj_sub", null)}
        </section>
        <section className="ip-card" aria-labelledby="pg-cal-t">
          <div className="ip-card-h"><h2 id="pg-cal-t">{t("prog.calendar.titlu")}</h2><Lamurire text={t("prog.calendar.sub")} /></div>
          <div className="pg-rand-set">
            <div className="nm">{t("prog.calendar.start")}<Lamurire text={t("prog.calendar.start_sub", { d: data(impl.setari.start) })} /></div>
            <div><input type="date" className={`pg-d${form.start ? " schimbat" : ""}`} value={val("start") ?? ""} disabled={!potScrie}
                        aria-label={t("prog.calendar.start")} onChange={(e) => pune("start", e.target.value || null)} /></div>
          </div>
          <div className="pg-rand-set">
            <div className="nm">{t("prog.calendar.zile")}<Lamurire text={t("prog.calendar.zile_sub", { h: nr(val("ore_pe_zi") ?? 8) })} /></div>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
              {ZILE.map((z, i) => (
                <button key={z} type="button" className={`pg-zi${zile.includes(i) ? " on" : ""}`} disabled={!potScrie}
                        aria-pressed={zile.includes(i)}
                        onClick={() => {
                          const n = zile.includes(i) ? zile.filter((x) => x !== i) : [...zile, i].sort();
                          if (n.length) pune("zile_lucratoare", n);
                        }}>{t(z)}</button>
              ))}
            </div>
          </div>
          <div className="pg-rand-set">
            <div className="nm">{t("prog.calendar.sarbatori")}<Lamurire text={t("prog.calendar.sarbatori_sub")} /></div>
            <div><label className="pg-dot"><input type="checkbox" checked={val("sarbatori_ro") ?? true} disabled={!potScrie}
                                                 onChange={(e) => pune("sarbatori_ro", e.target.checked)} />{t("prog.calendar.sarbatori_da")}</label></div>
          </div>
          <div className="pg-rand-set">
            <div className="nm">{t("prog.calendar.libere")}<Lamurire text={t("prog.calendar.libere_sub")} /></div>
            <div className="pg-libere">
              {libere.map((l, i) => {
                const [a, b] = Array.isArray(l) ? l : [l, l];
                return (
                  <div key={i}>
                    <span className="bc-um">{t("prog.calendar.de_la")}</span>
                    <input type="date" className="pg-d" value={a} disabled={!potScrie} aria-label={t("prog.calendar.de_la")}
                           onChange={(e) => pune("libere", libere.map((x, j) => j === i ? [e.target.value, b > e.target.value ? b : e.target.value] as [string, string] : x))} />
                    <span className="bc-um">{t("prog.calendar.pana_la")}</span>
                    <input type="date" className="pg-d" value={b} disabled={!potScrie} aria-label={t("prog.calendar.pana_la")}
                           onChange={(e) => pune("libere", libere.map((x, j) => j === i ? [a, e.target.value >= a ? e.target.value : a] as [string, string] : x))} />
                    {potScrie && <button type="button" className="btn btng" aria-label={t("prog.calendar.sterge")}
                                         onClick={() => { const n = libere.filter((_, j) => j !== i); pune("libere", n.length ? n : null); }}>×</button>}
                  </div>
                );
              })}
              {potScrie && (
                <button type="button" className="btn" style={{ height: 28 }}
                        onClick={() => { const z = val("start") ?? impl.setari.start; pune("libere", [...libere, [z, z]]); }}>
                  {t("prog.calendar.adauga")}</button>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
